import * as React from 'react';
import type { Editor } from '@tiptap/react';
import { createTranscriptCleanup, decideTranscriptCleanup, type TranscriptCleanup } from '@cdf/api-client-react';
import { browserTranscriptionSupported, startBrowserTranscription } from '@/lib/browser-voice';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { getErrorMessage } from '@/lib/utils';

/** Text for the editor: one paragraph per blank-line-separated block. */
function paragraphs(text: string) {
  return text.split(/\n{2,}/).map(block => block.trim()).filter(Boolean)
    .map(block => ({ type: 'paragraph', content: [{ type: 'text', text: block.replace(/\n/g, ' ') }] }));
}

export function DocumentDictation({editor,disabled,documentId}:{editor:Editor;disabled:boolean;documentId?:string}) {
  const [transcript,setTranscript]=React.useState(''),[recording,setRecording]=React.useState(false),[error,setError]=React.useState('');
  const [cleanup,setCleanup]=React.useState<TranscriptCleanup>(),[tidying,setTidying]=React.useState(false);
  const stopRef=React.useRef<(()=>void)|null>(null),epoch=React.useRef(0);
  const stop=React.useCallback(()=>{epoch.current++;stopRef.current?.();stopRef.current=null;setRecording(false);},[]);
  React.useEffect(()=>()=>{epoch.current++;stopRef.current?.();},[]);
  React.useEffect(()=>{if(disabled)stop();},[disabled,stop]);
  React.useEffect(()=>{
    if(!transcript)return;
    const unload=(event:BeforeUnloadEvent)=>{event.preventDefault();event.returnValue='';};
    const scope=(event:Event)=>{if(!window.confirm('Discard the retained transcript and switch context?'))event.preventDefault();};
    window.addEventListener('beforeunload',unload);window.addEventListener('cdf:before-scope-change',scope);
    return ()=>{window.removeEventListener('beforeunload',unload);window.removeEventListener('cdf:before-scope-change',scope);};
  },[transcript]);
  function start() {
    if(disabled||stopRef.current)return;
    const captured=++epoch.current;setError('');setRecording(true);
    stopRef.current=startBrowserTranscription(text=>{if(epoch.current===captured)setTranscript(value=>(value+(value?'\n':'')+text).slice(0,20000));},
      ()=>{if(epoch.current===captured){stopRef.current=null;setRecording(false);}},message=>{if(epoch.current===captured)setError(message);});
  }
  async function tidy() {
    if(disabled||recording||tidying||!transcript.trim())return;
    setTidying(true);setError('');
    try { setCleanup(await createTranscriptCleanup({requestId:crypto.randomUUID(),transcript,...(documentId?{documentId}:{})})); }
    catch(cause){ setError('Clean-up failed: '+getErrorMessage(cause)+' Your transcript is unchanged.'); }
    finally { setTidying(false); }
  }
  async function decide(decision:'applied'|'discarded') {
    if(!cleanup)return;
    const proposal=cleanup;
    if(decision==='applied')setTranscript(proposal.cleaned.slice(0,20000));
    setCleanup(undefined);
    // The record is an audit of the reviewed choice; the transcript choice above already stands locally.
    try { await decideTranscriptCleanup(proposal.id,{decision}); } catch { /* A failed audit write never changes the person's chosen text. */ }
  }
  function insert() {
    const content=paragraphs(transcript);
    if(!content.length)return;
    const before=editor.state.doc;
    const [single]=content;
    const inserted=editor.chain().focus().insertContent(content.length===1&&single?single:content).run();
    if(inserted&&!before.eq(editor.state.doc))setTranscript('');else setError('Choose an editable insertion point. The transcript is retained.');
  }
  return <details className="rounded border p-2 print:hidden"><summary className="cursor-pointer text-sm">Dictation and retained transcript</summary>
    <div className="space-y-2 pt-2">
      <p className="text-xs text-muted-foreground">Start explicitly. Browser dictation may use your browser’s speech service. Text stays here until you insert it; maximum 20,000 characters. “Tidy with AI” shows a cleaned-up version for you to review first; nothing changes in the document until you insert it.</p>
      <Button type="button" size="sm" variant="outline" disabled={disabled||recording||!browserTranscriptionSupported()} onClick={start}>Start dictation</Button>
      <Button type="button" size="sm" variant="outline" disabled={!recording} onClick={stop}>Stop dictation</Button>
      {!browserTranscriptionSupported()&&<p>Browser dictation is unavailable. You can type or paste a transcript.</p>}
      <Textarea aria-label="Retained transcript" maxLength={20000} value={transcript} onChange={e=>setTranscript(e.target.value)} disabled={recording||Boolean(cleanup)}/>
      {cleanup&&<section aria-label="Tidied transcript review" className="space-y-2 rounded-md border bg-muted/30 p-2">
        <p className="text-sm font-medium">Review the tidied text</p>
        <div className="grid gap-2 md:grid-cols-2">
          <div><p className="text-xs text-muted-foreground">Original</p><p className="whitespace-pre-wrap text-sm">{cleanup.original}</p></div>
          <div><p className="text-xs text-muted-foreground">Tidied</p><p className="whitespace-pre-wrap text-sm">{cleanup.cleaned}</p></div>
        </div>
        <div className="flex flex-wrap gap-2"><Button type="button" size="sm" onClick={()=>void decide('applied')}>Use tidied text</Button>
          <Button type="button" size="sm" variant="outline" onClick={()=>void decide('discarded')}>Keep original</Button></div>
      </section>}
      <Button type="button" size="sm" variant="outline" disabled={disabled||recording||tidying||Boolean(cleanup)||!transcript.trim()} onClick={()=>void tidy()}>{tidying?'Tidying…':'Tidy with AI'}</Button>
      <Button type="button" size="sm" disabled={disabled||recording||Boolean(cleanup)||!transcript.trim()} onClick={insert}>Insert transcript at cursor</Button>
      <Button type="button" size="sm" variant="ghost" disabled={recording||!transcript||Boolean(cleanup)} onClick={()=>{if(window.confirm('Clear the retained transcript?'))setTranscript('');}}>Clear transcript</Button>
      {error&&<p role="alert">{error}</p>}
    </div>
  </details>;
}
